import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AboutService } from "../../services/About.service";
import { useI18n } from "../../i18n";

const SOCIAL_ICONS = {
  instagram: "📸",
  facebook: "📘",
  tiktok: "🎵",
  linkedin: "💼",
  youtube: "▶️",
  telegram: "✈️",
  whatsapp: "💬",
  website: "🌐",
};

const SOCIAL_NAMES = {
  instagram: "Instagram",
  facebook: "Facebook",
  tiktok: "TikTok",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  telegram: "Telegram",
  whatsapp: "WhatsApp",
  website: "Website",
};

/**
 * Public "Haqqında" səhifəsi (/about). Bütün məzmun super admin
 * panelindən ("Sistem → Haqqında səhifəsi") 3 dildə idarə olunur.
 */
const AboutPage = () => {
  const { t, tr } = useI18n();
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await AboutService.get();
        if (!cancelled) setAbout(data.about);
      } catch {
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (failed || !about) {
    return (
      <div className="max-w-lg mx-auto page pt-16">
        <div className="bg-white rounded-3xl shadow-xl p-10 text-center">
          <p className="text-5xl mb-4">ℹ️</p>
          <p className="text-xl font-bold text-gray-800">{t("about.not_available")}</p>
          <Link
            to="/stores"
            className="inline-block mt-6 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-2xl transition"
          >
            {t("store.back_to_stores")}
          </Link>
        </div>
      </div>
    );
  }

  const features = about.features || [];
  const steps = about.steps || [];
  const socials = about.socials || [];
  const address = tr(about.address);
  const hasContact =
    about.contact_email || about.contact_phone || address || socials.length > 0;

  return (
    <div className="max-w-5xl mx-auto page pt-10 pb-16 space-y-10">
      {/* Başlıq */}
      <header className="text-center text-white">
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-3">{tr(about.title)}</h1>
        {about.subtitle && (
          <p className="text-indigo-100 max-w-2xl mx-auto text-lg">{tr(about.subtitle)}</p>
        )}
      </header>

      {/* Şəkil + əsas mətn */}
      {(about.image || tr(about.content)) && (
        <section className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {about.image && (
            <img
              src={about.image}
              alt={tr(about.title)}
              className="w-full h-48 sm:h-72 object-cover"
            />
          )}
          {tr(about.content) && (
            <div
              className="p-6 sm:p-10 text-gray-700 leading-relaxed text-left
                [&_p]:mb-4 [&_p:last-child]:mb-0
                [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-gray-800 [&_h2]:mt-6 [&_h2]:mb-3
                [&_h3]:text-xl [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-5 [&_h3]:mb-2
                [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4
                [&_a]:text-indigo-600 [&_a]:underline [&_strong]:text-gray-900
                [&_blockquote]:border-l-4 [&_blockquote]:border-indigo-300 [&_blockquote]:pl-4 [&_blockquote]:italic"
              dangerouslySetInnerHTML={{ __html: tr(about.content) }}
            />
          )}
        </section>
      )}

      {/* Üstünlüklər */}
      {features.length > 0 && (
        <section>
          <h2 className="text-2xl font-extrabold text-white drop-shadow mb-4">
            {t("about.features_title")}
          </h2>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((feature, i) => (
              <article
                key={i}
                className="bg-white rounded-2xl shadow-lg p-6 text-left hover:-translate-y-1 hover:shadow-2xl transition-all duration-300"
              >
                {feature.icon && <p className="text-4xl mb-3">{feature.icon}</p>}
                <h3 className="text-lg font-bold text-gray-800">{tr(feature.title)}</h3>
                {feature.text && (
                  <p className="text-sm text-gray-600 mt-2">{tr(feature.text)}</p>
                )}
              </article>
            ))}
          </div>
        </section>
      )}

      {/* Necə işləyir */}
      {steps.length > 0 && (
        <section>
          <h2 className="text-2xl font-extrabold text-white drop-shadow mb-4">
            {t("about.steps_title")}
          </h2>
          <ol className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {steps.map((step, i) => (
              <li
                key={i}
                className="bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-6 text-left text-white flex gap-4"
              >
                <span className="w-10 h-10 shrink-0 rounded-full bg-white text-indigo-700 font-extrabold flex items-center justify-center">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <h3 className="font-bold text-lg">{tr(step.title)}</h3>
                  {step.text && <p className="text-sm text-white/80 mt-1">{tr(step.text)}</p>}
                </div>
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Əlaqə */}
      {hasContact && (
        <section className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 text-left">
          <h2 className="text-2xl font-extrabold text-gray-800 mb-5">
            {t("about.contact_title")}
          </h2>

          <dl className="grid gap-4 sm:grid-cols-3">
            {about.contact_email && (
              <div className="min-w-0">
                <dt className="text-xs font-semibold uppercase text-gray-400">{t("about.email")}</dt>
                <dd>
                  <a
                    href={`mailto:${about.contact_email}`}
                    className="font-semibold text-indigo-600 hover:underline break-all"
                  >
                    {about.contact_email}
                  </a>
                </dd>
              </div>
            )}
            {about.contact_phone && (
              <div>
                <dt className="text-xs font-semibold uppercase text-gray-400">{t("about.phone")}</dt>
                <dd>
                  <a
                    href={`tel:${about.contact_phone.replace(/\s+/g, "")}`}
                    className="font-semibold text-indigo-600 hover:underline"
                  >
                    {about.contact_phone}
                  </a>
                </dd>
              </div>
            )}
            {address && (
              <div>
                <dt className="text-xs font-semibold uppercase text-gray-400">{t("about.address")}</dt>
                <dd className="font-semibold text-gray-700">{address}</dd>
              </div>
            )}
          </dl>

          {socials.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {socials.map((social, i) => (
                <a
                  key={i}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-semibold text-sm px-4 py-2 rounded-xl transition"
                >
                  <span>{SOCIAL_ICONS[social.platform] || "🔗"}</span>
                  {SOCIAL_NAMES[social.platform] || social.platform}
                </a>
              ))}
            </div>
          )}
        </section>
      )}

      <div className="text-center">
        <Link
          to="/stores"
          className="inline-block bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-8 py-3.5 rounded-2xl shadow-lg active:scale-[0.98] transition"
        >
          {t("about.cta")}
        </Link>
      </div>
    </div>
  );
};

export default AboutPage;
